import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import Lightbox from "@/components/Lightbox";
import { useLang } from "@/context/LangContext";

export default function ImageSlider({ slides = [], alt = "" }) {
  const { t } = useLang();
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(false);
  const touchX = useRef(null);
  const count = slides.length;

  const go = useCallback((n) => setIdx((i) => (count ? (i + n + count) % count : 0)), [count]);

  useEffect(() => {
    if (count < 2) return;
    const onKey = (e) => {
      if (zoom) return;
      const tag = e.target?.tagName;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag) || e.target?.isContentEditable) return;
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, count, zoom]);

  useEffect(() => setIdx(0), [slides.length, slides[0]]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!count) {
    return (
      <div data-testid="slider-empty" className="flex aspect-[4/5] w-full items-center justify-center border border-bone/10 bg-ink-2 bp-grid-fine">
        <span className="font-mono text-[10px] tracking-[0.3em] text-bone-muted">{t("product.noPhoto")}</span>
      </div>
    );
  }

  const onTouchStart = (e) => (touchX.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };
  const open = () => { if (touchX.current == null) setZoom(true); };

  return (
    <div data-testid="image-slider" className="relative select-none">
      <div className="relative overflow-hidden border border-bone/10 bg-ink-2" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        <div className="flex transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" style={{ transform: `translateX(-${idx * 100}%)` }}>
          {slides.map((src, i) => (
            <img
              key={src + i}
              data-testid={`slider-image-${i}`}
              src={src}
              alt={`${alt} — ${i + 1}/${count}`}
              className="block h-auto w-full shrink-0 cursor-zoom-in"
              loading={i === 0 ? "eager" : "lazy"}
              draggable={false}
              onClick={open}
            />
          ))}
        </div>
        <button data-testid="slider-zoom" type="button" aria-label={t("lightbox.open")} onClick={() => setZoom(true)} className="absolute bottom-3 left-3 flex items-center gap-2 border border-bone/15 bg-ink/70 px-3 py-2 font-mono text-[9px] tracking-[0.25em] text-bone-dim backdrop-blur transition-colors hover:border-brass/60 hover:text-brass">
          <ZoomIn size={12} /> {t("lightbox.open")}
        </button>
        {count > 1 && (
          <>
            <button data-testid="slider-prev" type="button" aria-label={t("product.prevPhoto")} onClick={() => go(-1)} className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center border border-bone/20 bg-ink/70 text-bone backdrop-blur transition-colors hover:border-brass/60 hover:text-brass">
              <ChevronLeft size={16} />
            </button>
            <button data-testid="slider-next" type="button" aria-label={t("product.nextPhoto")} onClick={() => go(1)} className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center border border-bone/20 bg-ink/70 text-bone backdrop-blur transition-colors hover:border-brass/60 hover:text-brass">
              <ChevronRight size={16} />
            </button>
            <span data-testid="slider-counter" className="absolute bottom-3 right-3 border border-bone/15 bg-ink/70 px-2 py-1 font-mono text-[9px] tracking-[0.25em] text-bone-dim backdrop-blur">
              {String(idx + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}
            </span>
          </>
        )}
      </div>
      {zoom && <Lightbox slides={slides} index={idx} onIndex={setIdx} onClose={() => setZoom(false)} alt={alt} />}
      {count > 1 && (
        <div data-testid="slider-dots" className="mt-4 flex items-center justify-center gap-3">
          {slides.map((_, i) => (
            <button
              key={i}
              data-testid={`slider-dot-${i}`}
              type="button"
              aria-label={`${t("product.goToPhoto")} ${i + 1}`}
              aria-current={i === idx}
              onClick={() => setIdx(i)}
              className={`h-[3px] transition-all duration-300 ${i === idx ? "w-8 bg-brass" : "w-4 bg-bone/25 hover:bg-bone/50"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
